import { useFormik } from "formik";
import { useEffect, useState } from "react";
import MESSAGES from "../../../../common/data/constants/messages";
import { IPermission } from "../../../../common/data/dtos/permission";
import Button from "../../../../components/bootstrap/Button";
import Card, { CardFooterRight, CardTabItem } from "../../../../components/bootstrap/Card";
import FormGroup from "../../../../components/bootstrap/forms/FormGroup";
import Input from "../../../../components/bootstrap/forms/Input";
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle } from "../../../../components/bootstrap/Modal";
import Spinner from "../../../../components/bootstrap/Spinner";
import { showSuccess, showWarning } from "../../../../components/extras/Notifications";
import permissionService from "../../../../services/permission-service";

type PermissionFormValues = {
    id?: string;
    name: string;
    description: string;
};

interface Props {
    isModalOpened: boolean;
    selectedPermission: IPermission | null;
    onModalClose: () => void;
    onAfterSubmit: () => void;
}

export const PermissionModal = ({ isModalOpened, selectedPermission, onModalClose, onAfterSubmit }: Props) => {

    const formik = useFormik({
        initialValues: {
            name: '',
            description: '',
        },

        validate: (values) => {
            const errors: {
                name?: string;
            } = {};

            if (!values.name) 
                errors.name = MESSAGES.VALIDATION.REQUIRED_FIELD;

            return errors;
        },

        onSubmit: (values, { resetForm }) => {
            onFormSubmit(values);
            resetForm();
        },
    });

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

    useEffect(() => {
        resetForm();

        if (selectedPermission) 
            setFieldValues(selectedPermission);

    }, [isModalOpened]);

    const onFormSubmit = async (values: PermissionFormValues) => {
        if (selectedPermission) {
            setIsSubmitting(true);

            const response = await permissionService.updatePermission({
                id: selectedPermission.id,
                name: values.name,
                description: values.description,
                rowVersion: selectedPermission.rowVersion,
                status: selectedPermission.status
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.EDIT_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.EDITED);
        } else {
            setIsSubmitting(true);

            const response = await permissionService.createPermission({
                name: values.name,
                description: values.description,
            });

            setIsSubmitting(false);

            if (response.error) {
                showWarning(MESSAGES.ACTION.SAVE_ERROR);
                return;
            }

            showSuccess(MESSAGES.ACTION.SAVED);
        }

        resetForm();
        onAfterSubmit();
    };

    const onClose = () => {
        onModalClose();
    };

    const resetForm = () => {
        formik.resetForm({
            values: {
                name: '',
                description: '',
            },
        });
    };

    const setFieldValues = (values: IPermission) => {
        formik.setFieldValue('name', values.name || '');
        formik.setFieldValue('description', values.description || '');
    };

    return (
        <Modal isOpen={isModalOpened} setIsOpen={onClose} size={'lg'} isCentered isStaticBackdrop>
            <ModalHeader setIsOpen={onClose}>
                <ModalTitle id='permission-modal'>Manutenção de permissões</ModalTitle>
            </ModalHeader>
            <ModalBody>
                <Card hasTab tabButtonColor='primary'>
                    
                    <CardTabItem id='tab-item-1' title='Dados de permissões' icon='Lock'>

                        <div className='row g-4'>

                            <div className='col-lg-12 col-md-12 col-sm-12'>
                                <FormGroup id='name' label='Nome' isFloating>
                                    <Input
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        value={formik.values.name}
                                        isValid={formik.isValid}
                                        isTouched={formik.touched.name}
                                        invalidFeedback={formik.errors.name}
                                        validFeedback=''
                                    />
                                </FormGroup>
                            </div>

                            <div className='col-lg-12 col-md-12 col-sm-12'>
                                <FormGroup id='description' label='Descrição' isFloating>
                                    <Input 
                                        onChange={formik.handleChange} 
                                        onBlur={formik.handleBlur} 
                                        value={formik.values.description} />
                                </FormGroup>
                            </div>
                            
                        </div>
                    </CardTabItem>
                </Card>
            </ModalBody>
            <ModalFooter className='bg-transparent'>
                <CardFooterRight>
                    <Button color='success' className='w-100' onClick={formik.handleSubmit} isDisable={isSubmitting}>
                        {isSubmitting && <Spinner isSmall inButton isGrow />}
                        Salvar
                    </Button>
                </CardFooterRight>
            </ModalFooter>
        </Modal>
    );
}